import classNames from "classnames";
import moment from "moment";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { Col } from "react-bootstrap";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import Select from "react-select";
import { compose } from "redux";

import Button from "pages/_components/Button";
import Card from "pages/_components/Card/Card";
import Container from "pages/_components/Container";
import ContextMenu from "pages/_components/ContextMenu";
import FormattedAmount from "pages/_components/FormattedAmount";
import Head from "pages/_components/Head";
import Heading from "pages/_components/Heading";
import I18n from "pages/_components/I18n";
import InfoTag from "pages/_components/InfoTag";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import { resizableRoute } from "pages/_components/Resizable";
import TransactionTicket from "pages/_components/TransactionTicket";
import Pagination from "pages/_components/pagination/Pagination";

import { actions as formActions } from "reducers/form";
import {
    actions as multipleTransferActions,
    selectors as multipleTransferSelectors,
} from "reducers/multiple-transfers";
import { actions as layoutActions } from "reducers/layout";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as transactionsActions, selectors as transactionsSelectors } from "reducers/transactions";

import * as configUtils from "util/config";
import { isUxDashboardImprovementsEnabled, useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";
import { V3_SCROLL_ROOT_ID } from "v3/constants";

import { multipleTransfersPre } from "middleware/multiple-transfers";
import Spinner from "pages/_components/Spinner";
import InfiniteScroll from "react-infinite-scroll-component";
import { getAccountDescription } from "util/accounts";
import * as i18n from "util/i18n";
import statusTagColorBatch, { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import {
    getMultipleTransferDestinationLabelKey,
    getMultipleTransferDestinationLabelText,
    isMasspaymentsCmfApiEnabled,
    isMultipleTransferUserCancelAllowed,
    isValidCvuNumeric,
    MULTIPLE_TRANSFER_TICKET_CMF_DESTINATION_LABEL_I18N_KEY,
    MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY,
    normalizeMultipleTransferLineErrorsForModal,
    sanitizeMasspaymentCmfTicketTransferData,
} from "util/multipleTransfersCmf";
import { VALIDATION_ERROR } from "util/responses";
import { checkSign, exportGenericTicketPdfWithSuccessMessage, getGeneralTicketItem } from "util/transaction";
import { getTransferUxI18nKey } from "util/transferInternalTitleLabels";
import MultipleTransferIndividualTicketData from "./MultipleTransferIndividualTicketData";
import MultipleTransferTicketData from "./MultipleTransferTicketData";
import BatchDetailErrorsModal from "./components/BatchDetailErrorsModal";
import MultipleTransferContextMenu from "./components/MultipleTransferContextMenu";

const idActivity = "transfer.multiple.send";

const getMultipleTransferSummaryTitleKey = (transferType) => {
    const baseKey = `multiple.transfers.summary.${transferType}.title`;
    return transferType === "transferInternal" ? getTransferUxI18nKey(baseKey) : baseKey;
};

function MultipleTransferDetail({
    batch,
    match: { params },
    location: { state: locationState },
    location,
    dispatch,
    fetchingDownload,
    fetching,
    isDesktop,
    batchDetail,
    pageNumber,
    totalPages,
    bankConcepts,
    batchErrors,
    loggedUser,
    potentialSignaturesData,
    activeEnvironment,
    embeddedInV3Shell = false,
}) {
    const [statusFilter, setStatusFilter] = useState("TODOS");
    const [showErrorsModal, setShowErrorsModal] = useState(false);
    const [dataModal, setDataModal] = useState([]);
    const [batchData, setBatchData] = useState(null);
    const [activeTransfer, setActiveTransfer] = useState(null);
    const { id } = params;

    const displayedBatchId = batch != null && batch.idBatch != null ? String(batch.idBatch) : "";

    /**
     * Alineada al hook del shell v3; si no, en mobile queda doble barra (shell + Head).
     */
    const effectiveEmbeddedInV3Shell = embeddedInV3Shell || (isUxDashboardImprovementsEnabled() && !isDesktop);

    /**
     * Misma clave que {@code TransferMultipleDetailActivity} (API). Evita paginación corta si totalPage del
     * backend no coincide con la cantidad del lote en TODOS.
     */
    const rowsPerPageDetail = configUtils.getInteger("frontend.Transfers.maxItems.ToShow", 5);
    const quantityNum = batch?.quantity != null ? Number(batch.quantity) : NaN;
    const pagesFromBatchQuantity =
        statusFilter === "TODOS" && Number.isFinite(quantityNum) && quantityNum > 0 && rowsPerPageDetail > 0
            ? Math.ceil(quantityNum / rowsPerPageDetail)
            : 0;
    const pageNumberNum = Number(pageNumber) || 0;
    const totalPagesNum = Number(totalPages) || 0;
    const effectiveTotalPages =
        pagesFromBatchQuantity > 0 ? Math.max(totalPagesNum, pagesFromBatchQuantity) : totalPagesNum;

    const signTransaction = () => {
        const signTransactionData = (
            <MultipleTransferTicketData
                dispatch={dispatch}
                isDesktop={isDesktop}
                data={batchData}
                batch={{ ...batch, idTransactionStatus: batch?.batchStatus }}
            />
        );

        dispatch(push({ pathname: "/transactions/sign", state: { goBackState: location.pathname } }));
        dispatch(
            formActions.signTransactionData({
                idTransaction: batch?.transactionId,
                idActivity,
                idForm: "",
                content: signTransactionData,
                title: getMultipleTransfersI18nKey("multiple.transfers.ticket.title"),
                pendingDispatch: false,
            }),
        );

        dispatch(
            formActions.signTransactionPreview({
                idActivity,
                idTransaction: batch?.transactionId,
                idForm: "",
                ticketData: batchData,
            }),
        );
    };

    const validateSignTransaction = async () => {
        dispatch(multipleTransferActions.setFetching(true));
        const { type, data } = await multipleTransfersPre();
        if (type === "W") {
            const { NO_FIELD } = data.data;
            if (data.code === VALIDATION_ERROR) {
                dispatch(notificationActions.showNotification(NO_FIELD, "warning", ["multipleTransfer.detail"]));
            }
        } else {
            signTransaction();
        }
        dispatch(multipleTransferActions.setFetching(false));
    };

    const cancelTransaction = () => {
        const cancelTransactionData = (
            <MultipleTransferTicketData
                dispatch={dispatch}
                isDesktop={isDesktop}
                data={batchData}
                batch={{ ...batch, idTransactionStatus: batch?.batchStatus }}
            />
        );
        dispatch(push({ pathname: "/transactions/cancel", state: { goBackState: location.pathname } }));
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: batch?.transactionId,
                content: cancelTransactionData,
                title: getMultipleTransfersI18nKey("multiple.transfers.ticket.title"),
                force: true,
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity, idForm: "" }));
    };

    const handleDownload = () => {
        const { idBatch, fileName } = batch;
        dispatch(multipleTransferActions.downloadBatchDetail(idBatch, statusFilter, fileName));
    };

    const mobileRightContent = () => {
        const items = [];
        if (
            batch?.batchStatus === "PENDING" &&
            checkSign({ signatures: batch.signatures }, loggedUser, potentialSignaturesData)
        ) {
            items.push({
                label: "multiple.transfers.detail.sign",
                onClick: validateSignTransaction,
                icon: "Sign",
            });
        }
        if (batch?.batchStatus?.toUpperCase()?.startsWith("PENDING") && isMultipleTransferUserCancelAllowed(batch)) {
            items.push({
                label: "multiple.transfers.card.cancel",
                onClick: cancelTransaction,
                icon: "CancelList",
            });
        }
        return !isDesktop ? (
            <ContextMenu
                imageStyle="pr-0"
                color="white"
                items={[
                    ...items,
                    {
                        label: "echeq.batch.detail.button.downloadExcel",
                        onClick: handleDownload,
                        icon: "SendDown",
                    },
                ]}
            />
        ) : (
            undefined
        );
    };

    useEffect(() => {
        if (document.getElementById("app")) {
            document.getElementById("app").scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    useEffect(() => {
        if (batch) {
            dispatch(transactionsActions.getPotentialSignaturesData(batch.transactionId));
            setBatchData({
                id: {
                    value: String(batch.idBatch),
                    format: "string",
                },
                debitAccount: {
                    value: getAccountDescription(batch.account),
                    format: "string",
                },
                reference: {
                    value: batch.reference,
                    format: "string",
                },
                uploadDate: {
                    value: batch.creationDate,
                    format: "datetime",
                },
                executionDate: {
                    value: batch.processDate,
                    format: "date",
                },
                fileName: {
                    value: batch.fileName,
                    format: "string",
                },
                quantity: {
                    value: batch.quantity,
                    format: "string",
                },
                amount: {
                    value: batch.amount?.quantity,
                    format: `amount${i18n.get(`currency.label.${batch.amount?.currency || "ARS"}`)}`,
                },
                ...(isMasspaymentsCmfApiEnabled() &&
                batch.voucherNumber != null &&
                String(batch.voucherNumber).trim() !== ""
                    ? {
                          voucherNumber: {
                              value: String(batch.voucherNumber).trim(),
                              format: "string",
                          },
                      }
                    : {}),
            });
        }
    }, [batch, dispatch]);

    useEffect(() => {
        if (locationState?.statusDetail === "FINISHED_ERROR") {
            setStatusFilter("ERROR");
            dispatch(multipleTransferActions.getMultipleTransferById(id, null, "ERROR"));
        } else {
            dispatch(multipleTransferActions.getMultipleTransferById(id, null, statusFilter));
        }
        // eslint-disable-next-line
    }, [id, dispatch]);

    const handleBack = () => dispatch(push("/transfer/multiple"));

    const getTransferContent = (transfer) => {
        const content = [];
        if (batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") && transfer?.destinationName) {
            content.push(`${getMultipleTransferDestinationLabelText()} ${transfer.cbuDestination}`);
        }
        content.push(
            `${i18n.get("multiple.transfers.modal.concept.label")} ${transfer?.idBankConcepts}-${
                bankConcepts ? bankConcepts[transfer?.idBankConcepts] : ""
            }`,
        );
        if (batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") && transfer?.voucherNumber) {
            const voucherLabel = isMasspaymentsCmfApiEnabled()
                ? i18n.get(MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY)
                : i18n.get("multiple.transfers.individual.data.voucherNumber.label");
            content.push(`${voucherLabel}: ${transfer.voucherNumber}`);
        }
        return content.join("  |  ");
    };

    const fetchMoreTransactions = (currPageNumber, status) => {
        dispatch(
            multipleTransferActions.getMultipleTransferById(id, currPageNumber, status || statusFilter, isDesktop),
        );
    };

    const handleChangeState = (option) => {
        setStatusFilter(option?.value);
        fetchMoreTransactions(1, option.value);
    };

    const renderFilterButton = () => {
        const options = [
            { value: "TODOS", label: "TODOS" },
            { value: "SUCCESS", label: i18n.get(`multiple.transfers.status.success`).toUpperCase() },
            { value: "ERROR", label: i18n.get(`multiple.transfers.status.error`).toUpperCase() },
        ];

        return (
            <Container className="mb-3" rowClassName="justify-content-start">
                <Col sm={12} lg={3} md={3}>
                    <div className="form-group-text">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor="status" className="data-label">
                            <I18n id="multiple.transfers.status.label" />
                        </label>
                    </div>
                    <div className="input-group">
                        <Select
                            name="status"
                            className="slideFromBottom"
                            clearable={false}
                            searchable={isDesktop}
                            onChange={handleChangeState}
                            options={options}
                            optionClassName="needsclick"
                            placeholder=""
                            value={statusFilter}
                            defaultValue={statusFilter}
                            noResultsText={i18n.get("global.no.results")}
                        />
                    </div>
                </Col>
            </Container>
        );
    };

    const renderDetailErrors = () => (
        <>
            <hr />
            <Container rowClassName="">
                <Col xs={12} sm={6} lg={2}>
                    <Heading.DataGroup
                        containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                        labelClassName="data-label-medium-normal transfer-multiple__data"
                        dataClassName="transfer-multiple__data ml-2"
                        label="multiple.transfers.detail.header.successedTransfers"
                        data={batchErrors?.successes}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                    <Heading.DataGroup
                        containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                        labelClassName="data-label-medium-normal transfer-multiple__data"
                        dataClassName="transfer-multiple__data ml-2"
                        label={
                            isDesktop
                                ? "multiple.transfers.detail.header.amount"
                                : "multiple.transfers.detail.header.amount.success"
                        }
                        data={
                            <FormattedAmount
                                currency={batch?.amount.currency}
                                quantity={batchErrors?.amountSuccess}
                                cleanClassName
                                className="transfer-multiple__data d-flex justify-content-end"
                            />
                        }
                    />
                </Col>
            </Container>
            <Container rowClassName="">
                <Col xs={12} sm={6} lg={2}>
                    <Heading.DataGroup
                        containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                        labelClassName="data-label-medium-normal transfer-multiple__data"
                        dataClassName="transfer-multiple__data ml-2"
                        label="multiple.transfers.detail.header.failedTransfers"
                        data={batchErrors?.errors}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3}>
                    <Heading.DataGroup
                        containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                        labelClassName="data-label-medium-normal transfer-multiple__data"
                        dataClassName="transfer-multiple__data ml-2"
                        label={
                            isDesktop
                                ? "multiple.transfers.detail.header.amount"
                                : "multiple.transfers.detail.header.amount.failed"
                        }
                        data={
                            <FormattedAmount
                                currency={batch?.amount.currency}
                                quantity={batchErrors?.amountError}
                                cleanClassName
                                className="transfer-multiple__data d-flex justify-content-end"
                            />
                        }
                    />
                </Col>
            </Container>
        </>
    );

    const toggleModal = (isVisible, data) => {
        setShowErrorsModal(isVisible);
        if (!isMasspaymentsCmfApiEnabled()) {
            if (!data) {
                setDataModal([]);
            } else if (typeof data === "string") {
                setDataModal(JSON.parse(data));
            } else {
                setDataModal(data);
            }
            return;
        }
        if (!data) {
            setDataModal([]);
            return;
        }
        if (typeof data === "string") {
            const trimmed = data.trim();
            if (!trimmed) {
                setDataModal([]);
                return;
            }
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    setDataModal(parsed);
                } else if (parsed != null && typeof parsed === "object") {
                    setDataModal([parsed]);
                } else {
                    setDataModal([]);
                }
            } catch (e) {
                setDataModal([{ code: "", description: trimmed }]);
            }
            return;
        }
        if (Array.isArray(data)) {
            setDataModal(data);
        } else if (data != null && typeof data === "object") {
            setDataModal([data]);
        } else {
            setDataModal([]);
        }
    };

    const openTransferLineErrorModal = (rawError) => {
        if (isMasspaymentsCmfApiEnabled()) {
            const normalized = normalizeMultipleTransferLineErrorsForModal(rawError);
            if (normalized != null) {
                toggleModal(true, normalized);
                return;
            }
        }
        toggleModal(true, rawError);
    };

    const generateTicketBody = (transferData) => {
        const cmf = isMasspaymentsCmfApiEnabled();
        const c = transferData.cbuDestination;
        const cbuStr = String((typeof c === "object" && c != null && c.value != null ? c.value : c) ?? "").trim();

        return Object.entries(transferData)
            .filter(([key, item]) => {
                if (!cmf || (key !== "bankName" && key !== "holder" && key !== "voucherNumber")) {
                    return true;
                }
                const v = item?.value !== undefined ? item.value : item;
                return String(v ?? "").trim() !== "";
            })
            .map(([key, item]) => {
                let labelKey = `multiple.transfers.summary.${key}.label`;
                if (cmf && key === "cbuDestination") {
                    labelKey = MULTIPLE_TRANSFER_TICKET_CMF_DESTINATION_LABEL_I18N_KEY;
                } else if (cmf && key === "bankName" && isValidCvuNumeric(cbuStr)) {
                    labelKey = "multiple.transfers.entityDestination.label";
                } else if (cmf && key === "voucherNumber") {
                    labelKey = MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY;
                }
                return getGeneralTicketItem(
                    labelKey,
                    key === "transferAmount" ? item.value?.quantity : item.value,
                    item.format,
                );
            });
    };

    const handleDownloadVoucher = async (transferData, isErrorVoucher = false) => {
        if (!transferData) {
            return;
        }
        dispatch(multipleTransferActions.setFetching(true));

        const cuitDestinationForCompare = isMasspaymentsCmfApiEnabled()
            ? transferData?.cuitCuiltDestination?.value
            : transferData?.cuitCuiltDestination?.value ?? transferData?.cuitCuiltDestination;

        const transferType =
            cuitDestinationForCompare === activeEnvironment?.clients[0]?.idClient
                ? "transferInternal"
                : "transferThirdParties";

        const transferDataForPdf =
            isMasspaymentsCmfApiEnabled() && transferData
                ? sanitizeMasspaymentCmfTicketTransferData(transferData)
                : transferData;

        const ticketData = {
            ticketName: isErrorVoucher
                ? i18n.get("multiple.transfers.summary.errorVoucher.title")
                : i18n.get(getMultipleTransferSummaryTitleKey(transferType)),
            ticketBody: generateTicketBody(transferDataForPdf),
        };

        await exportGenericTicketPdfWithSuccessMessage(ticketData, dispatch, i18n.get("global.download.success"));

        dispatch(multipleTransferActions.setFetching(false));
    };

    const buildVoucherShellMenu = (transferSnapshot) => (
        <ContextMenu
            imageStyle="pr-0"
            color="white"
            items={[
                {
                    label:
                        transferSnapshot?.status === "ERROR"
                            ? "multiple.transfers.detail.context.downloadErrorVoucher"
                            : "multiple.transfers.detail.context.download",
                    onClick: () => {
                        if (!transferSnapshot) {
                            return;
                        }
                        const { status: lineStatus, ...dataForPdf } = transferSnapshot;
                        handleDownloadVoucher(dataForPdf, lineStatus === "ERROR");
                    },
                    icon: "SendDown",
                },
            ]}
        />
    );

    let shellRightContentMobile = null;
    if (!isDesktop) {
        shellRightContentMobile = activeTransfer ? buildVoucherShellMenu(activeTransfer) : mobileRightContent();
    }

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell: isUxDashboardImprovementsEnabled(),
        isDesktop,
        scopes: ["multipleTransfer.detail"],
        headerTitle: `${i18n.get("multiple.transfers.idBatch.label")}: ${displayedBatchId}`,
        goBackPath: "/transfer/multiple",
        rightContentMobile: shellRightContentMobile,
    });

    /** Tras montar el ticket, `TransactionTicket` sincroniza el shell; se reafirma el menú de comprobante en mobile. */
    useLayoutEffect(() => {
        if (isDesktop || !isUxDashboardImprovementsEnabled() || !activeTransfer) {
            return;
        }
        dispatch(layoutActions.setRightContentMobile(buildVoucherShellMenu(activeTransfer)));
    }, [activeTransfer, isDesktop, dispatch]);

    const renderTransfers = () =>
        batchDetail?.map((transfer) => (
            <Card
                key={transfer.id}
                id={transfer.id}
                isDesktop={isDesktop}
                title={
                    <div className="transfer-multiple__detail-list-head">
                        <div className="d-flex space-between align-items-center w-100">
                            <div className="d-flex align-items-center flex-wrap">
                                <h3 className="mr-2">{`${i18n.get("multiple.transfers.modal.document.label")} ${
                                    transfer.cuitCuiltDestination
                                }`}</h3>
                                {batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") &&
                                transfer?.destinationName ? (
                                    <InfoTag
                                        type="info"
                                        message={`${i18n.get("multiple.transfers.detail.column.beneficiary")} ${
                                            transfer.destinationName
                                        }`}
                                        tagBackground="#E1F5FC"
                                        capitalize
                                        moreStyles={{
                                            alignSelf: "flex-start",
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                ) : (
                                    <InfoTag
                                        type="info"
                                        message={`${getMultipleTransferDestinationLabelText()} ${
                                            transfer.cbuDestination
                                        }`}
                                        tagBackground="#E1F5FC"
                                        capitalize
                                        moreStyles={{
                                            alignSelf: "flex-start",
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                )}
                                {transfer?.reference ? (
                                    <InfoTag
                                        type="info"
                                        message={`${i18n.get("multiple.transfers.modal.reference.label")} ${
                                            transfer.reference
                                        }`}
                                        tagBackground="#E8EAF6"
                                        capitalize
                                        tagClass="ml-2"
                                        moreStyles={{
                                            alignSelf: "flex-start",
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                ) : (
                                    undefined
                                )}
                                {isMasspaymentsCmfApiEnabled() && displayedBatchId ? (
                                    <InfoTag
                                        type="info"
                                        message={`${i18n.get("multiple.transfers.idBatch.label")}: ${displayedBatchId}`}
                                        tagBackground="#F5F5F5"
                                        capitalize
                                        tagClass="ml-2"
                                        moreStyles={{
                                            alignSelf: "flex-start",
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                }
                content={<span className="text-12 text-bold">{getTransferContent(transfer)}</span>}
                rightContent={
                    <div className="d-flex align-items-center">
                        <div className="d-flex flex-column align-items-end">
                            <FormattedAmount
                                currency={batch?.amount.currency}
                                quantity={transfer.amount}
                                className="transfer-multiple__amount"
                            />
                            {batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") && transfer?.status ? (
                                <InfoTag
                                    type="info"
                                    message={i18n.get(`multiple.transfers.status.${transfer?.status?.toLowerCase()}`)}
                                    tagBackground={statusTagColorBatch(transfer?.status)}
                                    tagClass="text-12 d-flex align-self-flex-end widht max-width-mincontent no-wrap py-0 px-3 mx-0"
                                    moreStyles={{
                                        color: "black",
                                        fontFamily: "Graphik-Regular",
                                    }}
                                />
                            ) : (
                                undefined
                            )}
                        </div>
                        <MultipleTransferContextMenu
                            dispatch={dispatch}
                            isDesktop={isDesktop}
                            batch={batch}
                            transfer={transfer}
                            bankConcepts={bankConcepts}
                            setActiveTransfer={setActiveTransfer}
                            handleDownloadVoucher={handleDownloadVoucher}
                            isBatchListLayout={false}
                            signGoBackPath={location.pathname}
                            action={() => {
                                if (transfer?.status === "ERROR") {
                                    return openTransferLineErrorModal(transfer?.error);
                                }
                                return undefined;
                            }}
                            idTransactionStatus={transfer?.status}
                        />
                    </div>
                }
            />
        ));

    const renderMobileTransfers = () =>
        batchDetail?.map((transfer) => (
            <Card
                key={transfer.id}
                id={transfer.id}
                isDesktop={isDesktop}
                title={
                    <div className="transfer-multiple__detail-list-head">
                        <div className="d-flex space-between align-items-center w-100">
                            <div className="d-flex align-items-center flex-wrap">
                                <h3>{`${i18n.get("multiple.transfers.modal.document.label")} ${
                                    transfer.cuitCuiltDestination
                                }`}</h3>
                                {isMasspaymentsCmfApiEnabled() && displayedBatchId ? (
                                    <InfoTag
                                        type="info"
                                        message={`${i18n.get("multiple.transfers.idBatch.label")}: ${displayedBatchId}`}
                                        tagBackground="#F5F5F5"
                                        capitalize
                                        tagClass="ml-2 mt-1"
                                        moreStyles={{
                                            alignSelf: "flex-start",
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                ) : null}
                            </div>
                            {transfer?.status === "SUCCESS" || transfer?.error ? (
                                <MultipleTransferContextMenu
                                    dispatch={dispatch}
                                    isDesktop={isDesktop}
                                    batch={batch}
                                    transfer={transfer}
                                    bankConcepts={bankConcepts}
                                    setActiveTransfer={setActiveTransfer}
                                    handleDownloadVoucher={handleDownloadVoucher}
                                    isBatchListLayout={false}
                                    signGoBackPath={location.pathname}
                                    action={() => {
                                        if (transfer?.status === "ERROR") {
                                            return openTransferLineErrorModal(transfer?.error);
                                        }
                                        return undefined;
                                    }}
                                    idTransactionStatus={transfer?.status}
                                />
                            ) : (
                                undefined
                            )}
                        </div>
                        <hr />
                    </div>
                }
                content={
                    <>
                        {transfer?.status ? (
                            <Heading.DataGroup
                                containerClassName="data-wrapper-flex space-between my-15"
                                labelClassName="data-label-medium text-12 text-bold"
                                dataClassName="data-desc text-12"
                                label="multiple.transfers.status.label"
                                data={
                                    <InfoTag
                                        type="info"
                                        message={i18n.get(
                                            `multiple.transfers.status.${transfer?.status?.toLowerCase()}`,
                                        )}
                                        tagBackground={statusTagColorBatch(transfer?.status)}
                                        tagClass="text-12 d-flex align-self-flex-end widht max-width-mincontent no-wrap py-0 px-3 m-0"
                                        moreStyles={{
                                            color: "black",
                                            fontFamily: "Graphik-Regular",
                                        }}
                                    />
                                }
                            />
                        ) : (
                            undefined
                        )}
                        {transfer?.destinationName && transfer?.status?.toUpperCase()?.startsWith("FINISHED") ? (
                            <Heading.DataGroup
                                containerClassName="data-wrapper-flex space-between my-15"
                                labelClassName="data-label-medium text-12 text-bold"
                                dataClassName="data-desc text-12"
                                label="multiple.transfers.detail.column.beneficiary"
                                data={transfer?.destinationName}
                            />
                        ) : (
                            undefined
                        )}
                        <Heading.DataGroup
                            containerClassName="data-wrapper-flex space-between my-15"
                            labelClassName="data-label-medium text-12 text-bold"
                            dataClassName="data-desc text-12"
                            label="multiple.transfers.reference.label"
                            data={transfer.reference}
                        />
                        <Heading.DataGroup
                            containerClassName="data-wrapper-flex space-between my-15"
                            labelClassName="data-label-medium text-12 text-bold"
                            dataClassName="data-desc text-12"
                            label={getMultipleTransferDestinationLabelKey()}
                            data={transfer.cbuDestination}
                        />
                        <Heading.DataGroup
                            containerClassName="data-wrapper-flex space-between my-15"
                            labelClassName="data-label-medium text-12 text-bold"
                            dataClassName="data-desc text-12"
                            label="multiple.transfers.summary.concept.label"
                            data={`${transfer?.idBankConcepts}-${
                                bankConcepts ? bankConcepts[transfer?.idBankConcepts] : ""
                            }`}
                        />
                        {transfer.voucherNumber ? (
                            <Heading.DataGroup
                                containerClassName="data-wrapper-flex space-between my-15"
                                labelClassName="data-label-medium text-12 text-bold"
                                dataClassName="data-desc text-12"
                                label={
                                    isMasspaymentsCmfApiEnabled()
                                        ? MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY
                                        : "multiple.transfers.individual.data.voucherNumber.label"
                                }
                                data={transfer.voucherNumber}
                            />
                        ) : (
                            undefined
                        )}
                    </>
                }
                rightContent={
                    <div className="d-flex align-items-center">
                        <div className="d-flex flex-column align-items-end">
                            <FormattedAmount
                                currency={batch?.amount.currency}
                                quantity={transfer.amount}
                                className="transfer-multiple__amount"
                            />
                        </div>
                    </div>
                }
            />
        ));

    const renderIndividualTransferTicket = () => {
        const cuitDestinationForCompare = isMasspaymentsCmfApiEnabled()
            ? activeTransfer?.cuitCuiltDestination?.value
            : activeTransfer?.cuitCuiltDestination?.value ?? activeTransfer?.cuitCuiltDestination;

        const transferType =
            cuitDestinationForCompare === activeEnvironment?.clients[0]?.idClient
                ? "transferInternal"
                : "transferThirdParties";

        const cuitCuilDestinationDisplay = isMasspaymentsCmfApiEnabled()
            ? activeTransfer.cuitCuiltDestination?.value
            : activeTransfer.cuitCuiltDestination?.value ?? activeTransfer.cuitCuiltDestination;

        const isLineError = activeTransfer?.status === "ERROR";
        const lineVoucher =
            activeTransfer.voucherNumber?.value != null && String(activeTransfer.voucherNumber?.value).trim() !== ""
                ? String(activeTransfer.voucherNumber?.value).trim()
                : null;
        const batchVoucher =
            isMasspaymentsCmfApiEnabled() && batch?.voucherNumber != null && String(batch.voucherNumber).trim() !== ""
                ? String(batch.voucherNumber).trim()
                : null;
        const voucherResolved = lineVoucher ?? batchVoucher ?? "";

        /** Mismo título que éxito (Figma); fallo vía subtítulo + icono (idTransactionStatus FAILED). */
        const summaryTitleKey = getMultipleTransferSummaryTitleKey(transferType);

        const ticketBodyData = {
            date: activeTransfer.transferDate?.value,
            voucherNumber: voucherResolved,
            originName: activeTransfer.originName?.value,
            debitAccount: activeTransfer.debitAccount?.value,
            transferType: activeTransfer.transferType?.value,
            cbuDestination: activeTransfer.cbuDestination?.value,
            cuitCuilDestination: cuitCuilDestinationDisplay,
            holder: activeTransfer.holder?.value,
            bankName: activeTransfer.bankName?.value,
            amount: activeTransfer.transferAmount?.value,
            concept: activeTransfer.concept?.value,
            reference: activeTransfer.transferReference?.value,
        };
        if (isLineError) {
            const code = activeTransfer.errorCode?.value;
            const desc = activeTransfer.errorDescription?.value;
            if (code != null && String(code).trim() !== "") {
                ticketBodyData.errorCode = code;
            }
            if (desc != null && String(desc).trim() !== "") {
                ticketBodyData.errorDescription = desc;
            }
        }

        return (
            <>
                {!effectiveEmbeddedInV3Shell && <Notification scopeToShow="multipleTransfer.detail" />}
                <TransactionTicket
                    embeddedInV3Shell={effectiveEmbeddedInV3Shell}
                    embeddedV3SkipRightContentMobileSync
                    embeddedV3GoBackPath={location.pathname}
                    notificationScope="multipleTransfer.detail"
                    handleBack={() => setActiveTransfer(null)}
                    handleClick={() => {
                        const { status: lineStatus, ...dataForPdf } = activeTransfer;
                        handleDownloadVoucher(dataForPdf, lineStatus === "ERROR");
                    }}
                    isFetchingExport={fetchingDownload}
                    handleClickMessage="global.download"
                    headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : ""}
                    centerElement={undefined}
                    downloadImageWhite={!isDesktop}
                    uniqueDownload={!isLineError}
                    fetching={fetching}
                    isDesktop={isDesktop}
                    summaryTitle={summaryTitleKey}
                    idSubtitle={
                        isLineError ? "forms.transaction.ticket.status.FAILED" : "transfer.multiple.individual.subtitle"
                    }
                    summary={{
                        ...activeTransfer,
                        idTransactionStatus: isLineError ? "FAILED" : activeTransfer?.status,
                    }}
                    hideMobileMenu={!isDesktop}
                    showDrawerMobile={false}
                    hideSigns
                    ignoreHomeButton>
                    <MultipleTransferIndividualTicketData isDesktop={isDesktop} data={ticketBodyData} />
                </TransactionTicket>
            </>
        );
    };

    if (activeTransfer) {
        return renderIndividualTransferTicket();
    }

    const renderAmountBlock = () => {
        let totalAmount;
        if (["PENDING", "CANCELLED", "PENDING_VALIDATION"].includes(batch?.batchStatus)) {
            totalAmount = batch?.amount?.quantity;
        } else {
            totalAmount = batch?.totalAmountValidated;
        }

        return (
            <div
                className={classNames("transfer-multiple__data-container py-2", {
                    "mb-3": !batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED"),
                })}>
                <Heading.DataGroup
                    containerClassName="data-wrapper-flex my-15 px-3"
                    labelClassName="data-label-medium transfer-multiple__amount"
                    dataClassName="ml-2"
                    label="multiple.transfers.amount.card"
                    data={
                        <FormattedAmount
                            currency={batch?.amount?.currency}
                            quantity={totalAmount}
                            small
                            className="transfer-multiple__amount d-flex justify-content-end"
                        />
                    }
                />

                {batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") &&
                    batchErrors?.errors > 0 &&
                    renderDetailErrors()}
            </div>
        );
    };

    const endOfListItem = (
        <div className="table-row min-height-0 justify-content-center" key="noMoreTransactions">
            <I18n id="transfers.multiple.finish.detail.list" componentProps={{ className: "my-2" }} />
        </div>
    );

    return (
        <PageLoading loading={(isDesktop && fetching) || fetchingDownload || (!isDesktop && fetching && !batch)}>
            {!effectiveEmbeddedInV3Shell && <Notification scopeToShow="multipleTransfer.detail" />}
            <div className="transfer-multiple__detail">
                <div className="admin-detail-head px-0">
                    {(isDesktop || !isUxDashboardImprovementsEnabled()) && (
                        <Head
                            onBack={handleBack}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : undefined}
                            titleText={
                                !isDesktop ? `${i18n.get("multiple.transfers.idBatch.label")}: ${displayedBatchId}` : ""
                            }
                            titleClassName="transfer-multiple__detail-title-mobile"
                            accessibilityTextId="transfers.main.label"
                            rightContent={mobileRightContent}
                        />
                    )}
                    {isDesktop ? (
                        <div className="transfer-multiple__header-desktop gap-2">
                            <div className="view-title d-flex flex-column">
                                <div className="d-flex">
                                    <I18n
                                        defaultValue={`${i18n.get(
                                            "multiple.transfers.idBatch.label",
                                        )}: ${displayedBatchId}`}
                                        component="h1"
                                    />
                                    <InfoTag
                                        type="info"
                                        message={i18n.get(
                                            `multiple.transfers.status.${batch?.batchStatus?.toLowerCase()}`,
                                        )}
                                        tagBackground={statusTagColorBatch(batch?.batchStatus)}
                                        tagClass="d-flex align-self-flex-end widht max-width-mincontent no-wrap py-1 px-2 ml-3"
                                        moreStyles={{
                                            color: "black",
                                        }}
                                    />
                                </div>
                                <Heading.DataGroup
                                    containerClassName="data-wrapper-flex"
                                    labelClassName="transfer-multiple__file-data-label"
                                    dataClassName="ml-2"
                                    label="multiple.transfers.summary.fileName.label"
                                    data={batch?.fileName}
                                    addDots
                                />
                            </div>
                            {batch?.batchStatus?.toUpperCase()?.startsWith("PENDING") &&
                            isMultipleTransferUserCancelAllowed(batch) ? (
                                <Button
                                    block
                                    label="multiple.transfers.card.cancel"
                                    loading={fetching}
                                    onClick={cancelTransaction}
                                    className="btn-primary"
                                />
                            ) : (
                                undefined
                            )}
                            {batch?.batchStatus === "PENDING" &&
                            checkSign({ signatures: batch.signatures }, loggedUser, potentialSignaturesData) ? (
                                <Button
                                    block
                                    label="multiple.transfers.detail.sign"
                                    loading={fetching}
                                    onClick={validateSignTransaction}
                                    className="btn-primary"
                                />
                            ) : (
                                undefined
                            )}
                            <Button
                                block
                                label="echeq.batch.detail.button.downloadExcel"
                                loading={fetchingDownload}
                                image="images/download_bold.svg"
                                onClick={handleDownload}
                                className="btn-outline cmf-button-width"
                                imageStyle="mr-2"
                            />
                        </div>
                    ) : (
                        <div className="px-3">
                            <div className="d-flex">
                                <InfoTag
                                    type="info"
                                    message={i18n.get(`multiple.transfers.status.${batch?.batchStatus?.toLowerCase()}`)}
                                    tagBackground={statusTagColorBatch(batch?.batchStatus)}
                                    tagClass="d-flex align-self-flex-end widht max-width-mincontent no-wrap py-1 px-2 mr-2 ml-0"
                                    moreStyles={{
                                        color: "black",
                                    }}
                                />
                            </div>
                            <Heading.DataGroup
                                containerClassName="data-wrapper-flex"
                                labelClassName="transfer-multiple__file-data-label"
                                dataClassName="transfer-multiple__file-data-label ml-2"
                                label="multiple.transfers.summary.fileName.label"
                                data={batch?.fileName}
                            />
                        </div>
                    )}
                </div>
                <div className="transfer-multiple__data-container">
                    <Container rowClassName="">
                        <Col xs={12} sm={6} lg={4}>
                            <Heading.DataGroup
                                containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                                labelClassName={`transfer-multiple__data ${isDesktop ? "data-label-medium" : ""}`}
                                dataClassName="transfer-multiple__data"
                                label="multiple.transfers.summary.account.label"
                                data={
                                    <div className="d-flex">
                                        <I18n
                                            defaultValue={batch?.account ? getAccountDescription(batch.account) : ""}
                                            componentProps={{ className: "transfer-multiple__data ml-1" }}
                                        />
                                    </div>
                                }
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4}>
                            <Heading.DataGroup
                                containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                                labelClassName={`transfer-multiple__data ${isDesktop ? "data-label-medium" : ""}`}
                                dataClassName="transfer-multiple__data ml-2"
                                label="multiple.transfers.summary.uploadDate.label"
                                data={moment(batch?.creationDate).format("DD/MM/yyyy")}
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4}>
                            <Heading.DataGroup
                                containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                                labelClassName={`transfer-multiple__data ${isDesktop ? "data-label-medium" : ""}`}
                                dataClassName="transfer-multiple__data ml-2"
                                label="multiple.transfers.summary.date.label"
                                data={moment(batch?.processDate).format("DD/MM/yyyy")}
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4} style={{ minWidth: 0 }}>
                            <Heading.DataGroup
                                containerClassName={`data-wrapper-flex align-items-baseline ${
                                    !isDesktop ? "space-between" : "my-15"
                                }`}
                                labelClassName={`transfer-multiple__data ${isDesktop ? "data-label-medium" : ""}`}
                                dataClassName="transfer-multiple__data ml-2"
                                label="multiple.transfers.reference.label"
                                data={
                                    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                        {batch?.reference}
                                    </span>
                                }
                            />
                        </Col>
                        <Col xs={12} sm={6} lg={4}>
                            <Heading.DataGroup
                                containerClassName={`data-wrapper-flex ${!isDesktop ? "space-between" : "my-15"}`}
                                labelClassName={`transfer-multiple__data ${isDesktop ? "data-label-medium" : ""}`}
                                dataClassName="transfer-multiple__data ml-2"
                                label="multiple.transfers.summary.quantity.label"
                                data={batch?.quantity}
                            />
                        </Col>
                    </Container>
                </div>
                {renderAmountBlock()}
                {batch?.batchStatus?.toUpperCase()?.startsWith("FINISHED") && batchErrors?.errors > 0
                    ? renderFilterButton()
                    : undefined}
                <BatchDetailErrorsModal
                    isDesktop={isDesktop}
                    isVisible={showErrorsModal}
                    toggleModal={toggleModal}
                    dataModal={dataModal}
                    className="multiple-transfer__modal-errors"
                />
                {isDesktop ? (
                    <>
                        <div className="transfer-multiple__detail-list">{renderTransfers()}</div>
                        <div className="d-flex w-100 justify-content-end mb-5">
                            <Pagination
                                totalPages={effectiveTotalPages}
                                pageNumber={pageNumberNum}
                                action={fetchMoreTransactions}
                            />
                        </div>
                    </>
                ) : (
                    <div className="transfer-multiple__detail-list">
                        <InfiniteScroll
                            dataLength={batchDetail?.length}
                            next={() => fetchMoreTransactions(pageNumberNum + 1)}
                            hasMore={pageNumberNum < effectiveTotalPages}
                            loader={<Spinner />}
                            endMessage={endOfListItem}
                            scrollableTarget={isUxDashboardImprovementsEnabled() ? V3_SCROLL_ROOT_ID : undefined}>
                            {renderMobileTransfers()}
                        </InfiniteScroll>
                    </div>
                )}
            </div>
        </PageLoading>
    );
}

const mapStateToProps = (state) => ({
    fetching: multipleTransferSelectors.getFetching(state),
    fetchingDownload: multipleTransferSelectors.getFetchingDownload(state),
    batch: multipleTransferSelectors.getActiveMultipleTransfer(state),
    batchDetail: multipleTransferSelectors.getBatchDetail(state),
    pageNumber: multipleTransferSelectors.getPageNumberDetail(state),
    totalPages: multipleTransferSelectors.getTotalPagesDetail(state),
    bankConcepts: multipleTransferSelectors.getBankConcepts(state),
    batchErrors: multipleTransferSelectors.getTransferBatchErrors(state),
    loggedUser: sessionSelectors.getUser(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
});

MultipleTransferDetail.propTypes = {
    match: shape({ params: shape({ id: string }) }).isRequired,
    location: shape({}).isRequired,
    embeddedInV3Shell: bool,
    batch: shape({}).isRequired,
    dispatch: func.isRequired,
    fetchingDownload: bool,
    fetching: bool.isRequired,
    isDesktop: bool.isRequired,
    batchDetail: arrayOf().isRequired,
    pageNumber: number.isRequired,
    totalPages: number.isRequired,
    bankConcepts: arrayOf().isRequired,
    batchErrors: shape({}).isRequired,
    loggedUser: shape({
        userId: string.isRequired,
    }).isRequired,
    potentialSignaturesData: arrayOf(shape({})).isRequired,
    activeEnvironment: shape({}).isRequired,
};

MultipleTransferDetail.defaultProps = {
    fetchingDownload: false,
    embeddedInV3Shell: false,
};

export default compose(connect(mapStateToProps), resizableRoute)(MultipleTransferDetail);
